import { parseLiveSteps } from '@lg/d365-client';
import { envProfile, type EnvProfile, type StepKey } from '@lg/shared';
import { SUPPORTED_LIVE_STEPS } from './api/remoteD365';

export interface AppConfig {
  /** api = call the Functions API; browser = in-browser mock backend (static demo build). */
  dataMode: 'api' | 'browser';
  /** msal = Entra sign-in; mock = demo persona switch. */
  authMode: 'msal' | 'mock';
  apiBaseUrl: string;
  entra: { tenantId: string; clientId: string; apiScope: string };
  /**
   * Provisioning steps that call the real D365 sandbox through the Cloudflare
   * Pages Function (VITE_D365_LIVE_STEPS). Empty = everything simulated.
   */
  liveSteps: StepKey[];
  /** The D365 environment the live steps target (VITE_ENV_PROFILE; default the RSM sandbox). */
  envProfile: EnvProfile;
  /**
   * Base URL of the Playwright HTML report from the acceptance-test repo (reports/demo-latest),
   * VITE_DEMO_REPORTS_URL. When set, readiness results link to their case in it.
   */
  demoReportsUrl: string | null;
}

/** Parse VITE_D365_LIVE_STEPS, keeping only steps the Pages Function can serve. */
export function parseConfiguredLiveSteps(value: string): StepKey[] {
  const steps = parseLiveSteps(value);
  const supported = steps.filter((s) => (SUPPORTED_LIVE_STEPS as readonly string[]).includes(s));
  const ignored = steps.filter((s) => !supported.includes(s));
  if (ignored.length) {
    console.warn(`VITE_D365_LIVE_STEPS: ${ignored.join(', ')} not available live yet; simulated instead.`);
  }
  return supported;
}

type ViteEnv = Record<string, string | boolean | undefined>;

export function readConfig(env: ViteEnv): AppConfig {
  const str = (key: string): string => {
    const v = env[key];
    return typeof v === 'string' ? v : '';
  };
  const dataMode = str('VITE_DATA_MODE') === 'browser' ? 'browser' : 'api';
  const requestedAuth = str('VITE_AUTH_MODE');
  // Dev defaults to mock auth; production builds default to Entra.
  const authMode =
    requestedAuth === 'mock' || requestedAuth === 'msal' ? requestedAuth : env['DEV'] ? 'mock' : 'msal';
  if (dataMode === 'browser' && authMode !== 'mock') {
    throw new Error('VITE_DATA_MODE=browser requires VITE_AUTH_MODE=mock');
  }
  return {
    dataMode,
    authMode,
    apiBaseUrl: str('VITE_API_BASE_URL') || '/api',
    entra: {
      tenantId: str('VITE_ENTRA_TENANT_ID'),
      clientId: str('VITE_ENTRA_CLIENT_ID'),
      apiScope: str('VITE_API_SCOPE'),
    },
    liveSteps: parseConfiguredLiveSteps(str('VITE_D365_LIVE_STEPS')),
    envProfile: envProfile(str('VITE_ENV_PROFILE') || undefined),
    demoReportsUrl: str('VITE_DEMO_REPORTS_URL').replace(/\/+$/, '') || null,
  };
}

export const config: AppConfig = readConfig(import.meta.env);
